import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Trash2, Code, FileText, Clock, Shield,
  CheckCircle, AlertCircle, Save, ChevronDown, ChevronUp,
  GripVertical, Settings, Eye, Sparkles, Loader2, X, Star,
  Zap, Crown, CreditCard
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { load } from '@cashfreepayments/cashfree-js';

const API = process.env.REACT_APP_BACKEND_URL;

// Available domains for AI question generation
const AI_DOMAINS = [
  { id: 'ai', label: 'AI / Machine Learning' },
  { id: 'ml', label: 'Machine Learning' },
  { id: 'dsa', label: 'Data Structures & Algorithms' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'cloud', label: 'Cloud Computing' },
  { id: 'blockchain', label: 'Blockchain' },
  { id: 'mathematics', label: 'Mathematics' },
  { id: 'logical', label: 'Logical Reasoning' }
];

const CreateAssessmentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Basic, 2: MCQs, 3: Coding, 4: Settings
  const [availableProblems, setAvailableProblems] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscribingPlan, setSubscribingPlan] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  
  // AI Question Generator State
  const [aiMcqLoading, setAiMcqLoading] = useState(false);
  const [aiCodingLoading, setAiCodingLoading] = useState(false);
  const [selectedMcqDomains, setSelectedMcqDomains] = useState(['dsa']);
  const [selectedCodingDomains, setSelectedCodingDomains] = useState(['dsa']);
  const [mcqDifficulty, setMcqDifficulty] = useState('medium');
  const [codingDifficulty, setCodingDifficulty] = useState('medium');
  const [mcqCount, setMcqCount] = useState(3);
  const [codingCount, setCodingCount] = useState(2);
  
  // AI Generated coding problems (stored separately before adding to assessment)
  const [aiGeneratedCodingProblems, setAiGeneratedCodingProblems] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration_minutes: 60,
    passing_percentage: 50,
    instructions: '',
    is_proctored: true,
    allow_copy_paste: false,
    show_results_immediately: true,
    mcqs: [],
    coding_problems: []
  });

  // MCQ being edited
  const [currentMCQ, setCurrentMCQ] = useState({
    question: '',
    options: [
      { id: 'opt_1', text: '', is_correct: false },
      { id: 'opt_2', text: '', is_correct: false },
      { id: 'opt_3', text: '', is_correct: false },
      { id: 'opt_4', text: '', is_correct: false }
    ],
    points: 5,
    explanation: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('company_token');
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchProblems(token);
    fetchPricing();
    fetchSubscriptionStatus(token);
    
    // Check for payment callback
    const paymentStatus = searchParams.get('payment_status');
    const orderId = searchParams.get('order_id');
    if (paymentStatus && orderId) {
      verifyPayment(orderId, paymentStatus, token);
    }
  }, [navigate, searchParams]);

  const verifyPayment = async (orderId, status, token) => {
    try {
      setPaymentProcessing(true);
      const res = await axios.post(
        `${API}/api/company/assessment-plans/verify-payment?order_id=${orderId}&payment_status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (status === 'success' && res.data.message?.includes('activated')) {
        toast.success(`🎉 ${res.data.plan_name} plan activated successfully!`);
        // Refresh subscription status
        fetchSubscriptionStatus(token);
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (status !== 'success') {
        toast.error('Payment was not successful. Please try again.');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      toast.error('Failed to verify payment');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    if (plan.price === 0) {
      toast.info('This is a free trial. No payment required.');
      return;
    }
    
    setSubscribingPlan(plan.id);
    const token = localStorage.getItem('company_token');
    
    try {
      // Create subscription order
      const res = await axios.post(
        `${API}/api/company/assessment-plans/subscribe?plan_id=${plan.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { payment_session_id, order_id, amount } = res.data;
      
      if (!payment_session_id) {
        toast.error('Could not initiate payment. Please try again.');
        setSubscribingPlan(null);
        return;
      }
      
      // Initialize Cashfree
      const cashfree = await load({ mode: 'production' });
      
      // Open Cashfree checkout
      await cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: '_self'
      });
      
    } catch (err) {
      console.error('Plan subscription error:', err);
      toast.error(err.response?.data?.detail || 'Failed to subscribe to plan');
      setSubscribingPlan(null);
    }
  };

  const fetchSubscriptionStatus = async (token) => {
    try {
      const res = await axios.get(`${API}/api/company/assessment-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    }
  };

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/api/public/company-pricing`);
      setPricing(res.data.pricing);
    } catch (err) {
      console.error('Failed to fetch pricing');
    }
  };

  const fetchProblems = async (token) => {
    try {
      // Fetch available coding problems from the platform
      const res = await axios.get(`${API}/api/coding/problems`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableProblems(res.data.problems || []);
    } catch (err) {
      console.log('Could not fetch problems:', err);
    }
  };

  const addMCQ = () => {
    if (!currentMCQ.question.trim()) {
      toast.error('Please enter a question');
      return;
    }
    if (!currentMCQ.options.some(o => o.is_correct)) {
      toast.error('Please mark at least one correct answer');
      return;
    }
    if (currentMCQ.options.filter(o => o.text.trim()).length < 2) {
      toast.error('Please add at least 2 options');
      return;
    }

    const mcqWithId = {
      ...currentMCQ,
      id: `mcq_${Date.now()}`,
      options: currentMCQ.options.filter(o => o.text.trim())
    };

    setFormData(prev => ({
      ...prev,
      mcqs: [...prev.mcqs, mcqWithId]
    }));

    // Reset form
    setCurrentMCQ({
      question: '',
      options: [
        { id: 'opt_1', text: '', is_correct: false },
        { id: 'opt_2', text: '', is_correct: false },
        { id: 'opt_3', text: '', is_correct: false },
        { id: 'opt_4', text: '', is_correct: false }
      ],
      points: 5,
      explanation: ''
    });

    toast.success('MCQ added');
  };

  const removeMCQ = (index) => {
    setFormData(prev => ({
      ...prev,
      mcqs: prev.mcqs.filter((_, i) => i !== index)
    }));
  };

  const toggleProblem = (problem) => {
    const exists = formData.coding_problems.find(p => p.problem_id === problem.id);
    if (exists) {
      setFormData(prev => ({
        ...prev,
        coding_problems: prev.coding_problems.filter(p => p.problem_id !== problem.id)
      }));
    } else {
      const points = problem.difficulty === 'easy' ? 10 : problem.difficulty === 'medium' ? 25 : 50;
      setFormData(prev => ({
        ...prev,
        coding_problems: [...prev.coding_problems, {
          problem_id: problem.id,
          title: problem.title,
          difficulty: problem.difficulty,
          points: points,
          order: prev.coding_problems.length + 1
        }]
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter assessment title');
      setStep(1);
      return;
    }
    if (formData.mcqs.length === 0 && formData.coding_problems.length === 0) {
      toast.error('Please add at least one MCQ or coding problem');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('company_token');
      const res = await axios.post(`${API}/api/company/assessments`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Assessment created successfully!');
      navigate(`/company/assessments/${res.data.assessment.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create assessment');
    } finally {
      setLoading(false);
    }
  };

  const totalPoints = formData.mcqs.reduce((sum, m) => sum + m.points, 0) + 
                      formData.coding_problems.reduce((sum, p) => sum + p.points, 0);

  const getDifficultyColor = (diff) => {
    switch (diff) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Payment Processing Overlay */}
      {paymentProcessing && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 text-center max-w-sm">
            <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
              <Loader2 className="text-purple-600 animate-spin" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Verifying Payment</h3>
            <p className="text-sm text-gray-600">Please wait while we confirm your subscription...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/company/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft size={20} />
              <span>Back to Dashboard</span>
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50"
              data-testid="create-assessment-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Save size={18} />
                  Create Assessment
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pricing Banner */}
        {pricing && pricing.assessment_plans && (
          <div className="mb-8 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-purple-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-purple-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Crown className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Assessment Plans</h3>
                  <p className="text-sm text-gray-600">Choose a plan to create and manage assessments</p>
                </div>
              </div>
              <button
                onClick={() => setShowPricingModal(true)}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium underline"
              >
                View Full Details
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-4 p-4">
              {pricing.assessment_plans.map((plan) => (
                <div 
                  key={plan.id}
                  className={`relative bg-white rounded-lg p-4 border-2 transition-all hover:shadow-md ${
                    plan.is_popular 
                      ? 'border-purple-500 shadow-lg shadow-purple-100' 
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  {plan.is_popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded-full flex items-center gap-1">
                      <Star size={12} fill="currentColor" /> Most Popular
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">{plan.name}</h4>
                    {plan.id === 'starter' && <Zap className="text-yellow-500" size={18} />}
                    {plan.id === 'professional' && <Star className="text-purple-500" size={18} />}
                    {plan.id === 'premium' && <Crown className="text-amber-500" size={18} />}
                  </div>
                  <div className="mb-3">
                    <span className="text-2xl font-bold text-gray-900">₹{plan.price.toLocaleString()}</span>
                    <span className="text-sm text-gray-500">/{plan.validity_days} days</span>
                  </div>
                  <ul className="space-y-1.5 text-sm text-gray-600">
                    {plan.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {plan.features.length > 3 && (
                      <li className="text-purple-600 text-xs font-medium">
                        +{plan.features.length - 3} more features
                      </li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
            {pricing.job_posting_free && (
              <div className="px-4 pb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                  <CheckCircle className="text-green-600" size={20} />
                  <p className="text-sm text-green-800">
                    <span className="font-semibold">Good news!</span> Job postings are FREE for all companies. Create assessments to screen your candidates.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Subscription Status Banner */}
        {subscriptionStatus && (
          <div className={`mb-8 rounded-xl border-2 overflow-hidden ${
            subscriptionStatus.needs_upgrade 
              ? 'bg-amber-50 border-amber-300' 
              : 'bg-white border-gray-200'
          }`}>
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  subscriptionStatus.needs_upgrade ? 'bg-amber-500' : 'bg-green-500'
                }`}>
                  {subscriptionStatus.needs_upgrade ? (
                    <AlertCircle className="text-white" size={24} />
                  ) : (
                    <CheckCircle className="text-white" size={24} />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">
                      {subscriptionStatus.current_plan?.name || 'Free Trial'}
                    </h3>
                    {subscriptionStatus.has_subscription && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                        Active
                      </span>
                    )}
                    {!subscriptionStatus.has_subscription && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                        No Plan
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {subscriptionStatus.billing_period?.days_remaining > 0 
                      ? `${subscriptionStatus.billing_period.days_remaining} days remaining in billing period`
                      : 'Billing period ended'
                    }
                  </p>
                </div>
              </div>
              
              {/* Usage Stats */}
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Assessments</p>
                  <p className="text-lg font-bold text-gray-900">
                    {subscriptionStatus.usage?.assessments_created || 0}
                    <span className="text-gray-400 font-normal">
                      /{subscriptionStatus.limits?.assessments === -1 ? '∞' : subscriptionStatus.limits?.assessments}
                    </span>
                  </p>
                  {subscriptionStatus.limits?.assessments !== -1 && (
                    <div className="w-20 h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          (subscriptionStatus.usage?.assessments_created / subscriptionStatus.limits?.assessments) >= 0.8 
                            ? 'bg-red-500' 
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(100, (subscriptionStatus.usage?.assessments_created / subscriptionStatus.limits?.assessments) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Participants</p>
                  <p className="text-lg font-bold text-gray-900">
                    {subscriptionStatus.usage?.participants_total || 0}
                    <span className="text-gray-400 font-normal">
                      /{subscriptionStatus.limits?.participants === -1 ? '∞' : subscriptionStatus.limits?.participants}
                    </span>
                  </p>
                  {subscriptionStatus.limits?.participants !== -1 && (
                    <div className="w-20 h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          (subscriptionStatus.usage?.participants_total / subscriptionStatus.limits?.participants) >= 0.8 
                            ? 'bg-red-500' 
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(100, (subscriptionStatus.usage?.participants_total / subscriptionStatus.limits?.participants) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                
                {subscriptionStatus.needs_upgrade && (
                  <button
                    onClick={() => setShowPricingModal(true)}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm flex items-center gap-2 transition-colors"
                  >
                    <Zap size={16} />
                    Upgrade Now
                  </button>
                )}
              </div>
            </div>
            
            {/* Warning Banner if limit reached */}
            {subscriptionStatus.needs_upgrade && (
              <div className="px-4 pb-4">
                <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 flex items-center gap-3">
                  <AlertCircle className="text-amber-600 flex-shrink-0" size={20} />
                  <p className="text-sm text-amber-800">
                    <span className="font-semibold">
                      {subscriptionStatus.upgrade_reason === 'assessment_limit' 
                        ? 'Assessment limit reached!' 
                        : 'Participant limit reached!'}
                    </span>
                    {' '}Upgrade your plan to create more assessments and invite more candidates.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pricing Modal */}
        {showPricingModal && pricing && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Assessment Pricing Plans</h2>
                  <p className="text-sm text-gray-600 mt-1">Choose the plan that fits your hiring needs</p>
                </div>
                <button 
                  onClick={() => setShowPricingModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-3 gap-6">
                  {pricing.assessment_plans.map((plan) => (
                    <div 
                      key={plan.id}
                      className={`relative rounded-xl p-6 border-2 transition-all ${
                        plan.is_popular 
                          ? 'border-purple-500 bg-purple-50/50' 
                          : 'border-gray-200'
                      }`}
                    >
                      {plan.is_popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-purple-600 text-white text-sm font-medium rounded-full">
                          Recommended
                        </div>
                      )}
                      <div className="text-center mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{plan.name}</h3>
                        <div>
                          <span className="text-4xl font-bold text-gray-900">₹{plan.price.toLocaleString()}</span>
                          <span className="text-gray-500">/{plan.validity_days} days</span>
                        </div>
                      </div>
                      <div className="space-y-4 mb-6">
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="text-gray-600">Assessments</span>
                          <span className="font-semibold text-gray-900">
                            {plan.assessments_limit === -1 ? 'Unlimited' : plan.assessments_limit}
                          </span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                          <span className="text-gray-600">Participants</span>
                          <span className="font-semibold text-gray-900">
                            {plan.participants_limit === -1 ? 'Unlimited' : plan.participants_limit}
                          </span>
                        </div>
                      </div>
                      <ul className="space-y-3">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <button 
                        onClick={() => handleSelectPlan(plan)}
                        disabled={subscribingPlan === plan.id || (subscriptionStatus?.current_plan?.id === plan.id && subscriptionStatus?.has_subscription)}
                        className={`w-full mt-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                          plan.is_popular
                            ? 'bg-purple-600 hover:bg-purple-700 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                        }`}
                      >
                        {subscribingPlan === plan.id ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Processing...
                          </>
                        ) : subscriptionStatus?.current_plan?.id === plan.id && subscriptionStatus?.has_subscription ? (
                          <>
                            <CheckCircle size={18} />
                            Current Plan
                          </>
                        ) : (
                          <>
                            <CreditCard size={18} />
                            Select {plan.name}
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 text-center">
                    Need a custom plan? <a href="/contact" className="text-purple-600 hover:underline font-medium">Contact our sales team</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { num: 1, label: 'Basic Info' },
            { num: 2, label: 'MCQs' },
            { num: 3, label: 'Coding' },
            { num: 4, label: 'Settings' }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <button
                onClick={() => setStep(s.num)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  step === s.num 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-purple-300'
                }`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s.num ? 'bg-white/20' : 'bg-gray-100'
                }`}>
                  {s.num}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {idx < 3 && <div className="w-8 h-0.5 bg-gray-200" />}
            </React.Fragment>
          ))}
        </div>

        {/* Summary Card */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-purple-200 text-sm">Total Points</p>
              <p className="text-2xl font-bold">{totalPoints}</p>
            </div>
            <div>
              <p className="text-purple-200 text-sm">MCQs</p>
              <p className="text-2xl font-bold">{formData.mcqs.length}</p>
            </div>
            <div>
              <p className="text-purple-200 text-sm">Coding Problems</p>
              <p className="text-2xl font-bold">{formData.coding_problems.length}</p>
            </div>
            <div>
              <p className="text-purple-200 text-sm">Duration</p>
              <p className="text-2xl font-bold">{formData.duration_minutes} min</p>
            </div>
          </div>
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Basic Information</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assessment Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Frontend Developer Assessment"
                  data-testid="assessment-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Brief description of this assessment..."
                  data-testid="assessment-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes) *</label>
                  <input
                    type="number"
                    min={15}
                    max={180}
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 60 }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="assessment-duration-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Passing Percentage *</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={formData.passing_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, passing_percentage: parseInt(e.target.value) || 50 }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="assessment-passing-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Instructions for Candidates</label>
                <textarea
                  value={formData.instructions}
                  onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter instructions that will be shown to candidates before they start..."
                  data-testid="assessment-instructions-input"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
              >
                Next: Add MCQs
              </button>
            </div>
          </div>
        )}

        {/* Step 2: MCQs */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Existing MCQs */}
            {formData.mcqs.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Added MCQs ({formData.mcqs.length})</h3>
                <div className="space-y-3">
                  {formData.mcqs.map((mcq, idx) => (
                    <div key={mcq.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Q{idx + 1}: {mcq.question}</p>
                        <p className="text-sm text-gray-500 mt-1">{mcq.options.length} options • {mcq.points} points</p>
                      </div>
                      <button
                        onClick={() => removeMCQ(idx)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI MCQ Question Generator */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="text-purple-600" size={24} />
                AI MCQ Generator
              </h3>
              <p className="text-sm text-gray-600 mb-4">Generate MCQ questions using AI - select multiple domains and difficulty level</p>
              
              {/* Multi-Domain Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Domains (one or more)</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {AI_DOMAINS.map(domain => (
                    <button
                      key={domain.id}
                      type="button"
                      onClick={() => {
                        setSelectedMcqDomains(prev => 
                          prev.includes(domain.id) 
                            ? prev.filter(d => d !== domain.id)
                            : [...prev, domain.id]
                        );
                      }}
                      className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                        selectedMcqDomains.includes(domain.id)
                          ? 'border-purple-500 bg-purple-100 text-purple-800'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-purple-300'
                      }`}
                      data-testid={`mcq-domain-${domain.id}`}
                    >
                      {selectedMcqDomains.includes(domain.id) && <CheckCircle className="inline mr-1" size={14} />}
                      {domain.label}
                    </button>
                  ))}
                </div>
                {selectedMcqDomains.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Please select at least one domain</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                  <select
                    value={mcqDifficulty}
                    onChange={(e) => setMcqDifficulty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                    data-testid="mcq-ai-difficulty"
                  >
                    <option value="easy">Easy (5 pts each)</option>
                    <option value="medium">Medium (10 pts each)</option>
                    <option value="hard">Hard (15 pts each)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Questions</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={mcqCount}
                    onChange={(e) => setMcqCount(Math.min(20, Math.max(1, parseInt(e.target.value) || 3)))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                    data-testid="mcq-ai-count"
                  />
                </div>
              </div>
              
              <button
                type="button"
                onClick={async () => {
                  if (selectedMcqDomains.length === 0) {
                    toast.error('Please select at least one domain');
                    return;
                  }
                  
                  setAiMcqLoading(true);
                  try {
                    const token = localStorage.getItem('company_token');
                    const res = await axios.post(`${API}/api/company/generate-questions`, {
                      question_type: 'mcq',
                      domains: selectedMcqDomains,
                      difficulty: mcqDifficulty,
                      count: mcqCount
                    }, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    if (res.data.generated_questions?.length > 0) {
                      // Add unique IDs to each question
                      const questionsWithIds = res.data.generated_questions.map((q, idx) => ({
                        ...q,
                        id: q.id || `mcq_ai_${Date.now()}_${idx}`
                      }));
                      
                      setFormData(prev => ({
                        ...prev,
                        mcqs: [...prev.mcqs, ...questionsWithIds]
                      }));
                      toast.success(`Added ${res.data.generated_questions.length} AI-generated MCQs from ${selectedMcqDomains.length} domain(s)`);
                    } else {
                      toast.error('No questions generated');
                    }
                  } catch (err) {
                    console.error('AI generation error:', err);
                    toast.error(err.response?.data?.detail || 'Failed to generate questions');
                  } finally {
                    setAiMcqLoading(false);
                  }
                }}
                disabled={aiMcqLoading || selectedMcqDomains.length === 0}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="generate-mcqs-btn"
              >
                {aiMcqLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Generating MCQs...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Generate {mcqCount} MCQs
                  </>
                )}
              </button>
            </div>

            {/* Add New MCQ */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Add New MCQ</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Question *</label>
                  <textarea
                    value={currentMCQ.question}
                    onChange={(e) => setCurrentMCQ(prev => ({ ...prev, question: e.target.value }))}
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Enter your question..."
                    data-testid="mcq-question-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Options (mark correct answer)</label>
                  <div className="space-y-2">
                    {currentMCQ.options.map((opt, idx) => (
                      <div key={opt.id} className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setCurrentMCQ(prev => ({
                              ...prev,
                              options: prev.options.map((o, i) => ({
                                ...o,
                                is_correct: i === idx
                              }))
                            }));
                          }}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                            opt.is_correct 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : 'border-gray-300 hover:border-green-400'
                          }`}
                        >
                          {opt.is_correct && <CheckCircle size={14} />}
                        </button>
                        <input
                          type="text"
                          value={opt.text}
                          onChange={(e) => {
                            setCurrentMCQ(prev => ({
                              ...prev,
                              options: prev.options.map((o, i) => 
                                i === idx ? { ...o, text: e.target.value } : o
                              )
                            }));
                          }}
                          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder={`Option ${idx + 1}`}
                          data-testid={`mcq-option-${idx}-input`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Points</label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={currentMCQ.points}
                      onChange={(e) => setCurrentMCQ(prev => ({ ...prev, points: parseInt(e.target.value) || 5 }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      data-testid="mcq-points-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Explanation (Optional)</label>
                    <input
                      type="text"
                      value={currentMCQ.explanation}
                      onChange={(e) => setCurrentMCQ(prev => ({ ...prev, explanation: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Explain the correct answer..."
                    />
                  </div>
                </div>

                <button
                  onClick={addMCQ}
                  className="w-full py-3 border-2 border-dashed border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50 font-medium flex items-center justify-center gap-2"
                  data-testid="add-mcq-btn"
                >
                  <Plus size={18} />
                  Add MCQ
                </button>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-2 border border-gray-200 rounded-lg font-medium hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
              >
                Next: Add Coding Problems
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Coding Problems */}
        {step === 3 && (
          <div className="space-y-6">
            {/* Selected Problems */}
            {formData.coding_problems.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Selected Problems ({formData.coding_problems.length})</h3>
                <div className="space-y-2">
                  {formData.coding_problems.map((problem, idx) => (
                    <div key={problem.problem_id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-purple-600 text-white rounded text-sm flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <span className="font-medium text-gray-900">{problem.title}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getDifficultyColor(problem.difficulty)}`}>
                          {problem.difficulty?.toUpperCase()}
                        </span>
                        {problem.is_ai_generated && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                            AI Generated
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-purple-600 font-medium">{problem.points} pts</span>
                        <button
                          onClick={() => {
                            // Remove from both regular toggle and ai generated
                            if (problem.is_ai_generated) {
                              setFormData(prev => ({
                                ...prev,
                                coding_problems: prev.coding_problems.filter(p => p.problem_id !== problem.problem_id)
                              }));
                            } else {
                              toggleProblem({ id: problem.problem_id });
                            }
                          }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Coding Question Generator */}
            <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl border-2 border-green-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Code className="text-green-600" size={24} />
                AI Coding Problem Generator
              </h3>
              <p className="text-sm text-gray-600 mb-4">Generate coding challenges with test cases using AI - select multiple domains</p>
              
              {/* Multi-Domain Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Domains (one or more)</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {AI_DOMAINS.map(domain => (
                    <button
                      key={domain.id}
                      type="button"
                      onClick={() => {
                        setSelectedCodingDomains(prev => 
                          prev.includes(domain.id) 
                            ? prev.filter(d => d !== domain.id)
                            : [...prev, domain.id]
                        );
                      }}
                      className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                        selectedCodingDomains.includes(domain.id)
                          ? 'border-green-500 bg-green-100 text-green-800'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-green-300'
                      }`}
                      data-testid={`coding-domain-${domain.id}`}
                    >
                      {selectedCodingDomains.includes(domain.id) && <CheckCircle className="inline mr-1" size={14} />}
                      {domain.label}
                    </button>
                  ))}
                </div>
                {selectedCodingDomains.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Please select at least one domain</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                  <select
                    value={codingDifficulty}
                    onChange={(e) => setCodingDifficulty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                    data-testid="coding-ai-difficulty"
                  >
                    <option value="easy">Easy (50 pts each)</option>
                    <option value="medium">Medium (100 pts each)</option>
                    <option value="hard">Hard (150 pts each)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Problems</label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={codingCount}
                    onChange={(e) => setCodingCount(Math.min(10, Math.max(1, parseInt(e.target.value) || 2)))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                    data-testid="coding-ai-count"
                  />
                </div>
              </div>
              
              <button
                type="button"
                onClick={async () => {
                  if (selectedCodingDomains.length === 0) {
                    toast.error('Please select at least one domain');
                    return;
                  }
                  
                  setAiCodingLoading(true);
                  try {
                    const token = localStorage.getItem('company_token');
                    const res = await axios.post(`${API}/api/company/generate-questions`, {
                      question_type: 'coding',
                      domains: selectedCodingDomains,
                      difficulty: codingDifficulty,
                      count: codingCount
                    }, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    if (res.data.generated_questions?.length > 0) {
                      // Convert AI generated coding problems to the format expected by the assessment
                      const codingProblems = res.data.generated_questions.map((q, idx) => ({
                        problem_id: q.problem_id || `ai_coding_${Date.now()}_${idx}`,
                        title: q.title,
                        description: q.description,
                        difficulty: q.difficulty,
                        points: q.points,
                        domain: q.domain,
                        constraints: q.constraints,
                        sample_input: q.sample_input,
                        sample_output: q.sample_output,
                        explanation: q.explanation,
                        hidden_test_cases: q.hidden_test_cases || [],
                        time_limit_ms: q.time_limit_ms || 2000,
                        memory_limit_mb: q.memory_limit_mb || 256,
                        is_ai_generated: true,
                        order: formData.coding_problems.length + idx + 1
                      }));
                      
                      setFormData(prev => ({
                        ...prev,
                        coding_problems: [...prev.coding_problems, ...codingProblems]
                      }));
                      
                      toast.success(`Added ${res.data.generated_questions.length} AI-generated coding problems from ${selectedCodingDomains.length} domain(s)`);
                    } else {
                      toast.error('No coding problems generated');
                    }
                  } catch (err) {
                    console.error('AI generation error:', err);
                    toast.error(err.response?.data?.detail || 'Failed to generate coding problems');
                  } finally {
                    setAiCodingLoading(false);
                  }
                }}
                disabled={aiCodingLoading || selectedCodingDomains.length === 0}
                className="w-full py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-teal-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="generate-coding-btn"
              >
                {aiCodingLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Generating Coding Problems...
                  </>
                ) : (
                  <>
                    <Code size={18} />
                    Generate {codingCount} Coding Problem{codingCount > 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>

            {/* Available Problems */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Available Problems</h3>
              {availableProblems.length > 0 ? (
                <div className="space-y-2">
                  {availableProblems.map(problem => {
                    const isSelected = formData.coding_problems.some(p => p.problem_id === problem.id);
                    return (
                      <button
                        key={problem.id}
                        onClick={() => toggleProblem(problem)}
                        className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                          isSelected 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                        data-testid={`problem-${problem.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                          }`}>
                            {isSelected && <CheckCircle size={12} className="text-white" />}
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-gray-900">{problem.title}</p>
                            <p className="text-sm text-gray-500">{problem.tags?.slice(0, 3).join(', ')}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(problem.difficulty)}`}>
                            {problem.difficulty?.toUpperCase()}
                          </span>
                          <Code size={16} className="text-gray-400" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Code className="mx-auto text-gray-300 mb-3" size={40} />
                  <p className="text-gray-500">No coding problems available</p>
                  <p className="text-sm text-gray-400 mt-1">Problems need to be created in the admin panel first</p>
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-2 border border-gray-200 rounded-lg font-medium hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={() => setStep(4)}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
              >
                Next: Settings
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Settings */}
        {step === 4 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Assessment Settings</h2>
            
            <div className="space-y-6">
              {/* Proctoring */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield className="text-purple-600" size={24} />
                  <div>
                    <p className="font-medium text-gray-900">Enable Proctoring</p>
                    <p className="text-sm text-gray-500">Detect tab switches and copy-paste attempts</p>
                  </div>
                </div>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, is_proctored: !prev.is_proctored }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    formData.is_proctored ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                  data-testid="proctoring-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    formData.is_proctored ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Copy Paste */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="text-blue-600" size={24} />
                  <div>
                    <p className="font-medium text-gray-900">Allow Copy-Paste</p>
                    <p className="text-sm text-gray-500">Let candidates copy-paste in code editor</p>
                  </div>
                </div>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, allow_copy_paste: !prev.allow_copy_paste }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    formData.allow_copy_paste ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                  data-testid="copy-paste-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    formData.allow_copy_paste ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Show Results */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Eye className="text-green-600" size={24} />
                  <div>
                    <p className="font-medium text-gray-900">Show Results Immediately</p>
                    <p className="text-sm text-gray-500">Show score to candidates after submission</p>
                  </div>
                </div>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, show_results_immediately: !prev.show_results_immediately }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    formData.show_results_immediately ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                  data-testid="show-results-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    formData.show_results_immediately ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>

            <div className="mt-8 p-4 bg-purple-50 rounded-lg">
              <h4 className="font-medium text-purple-900 mb-2">Assessment Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-purple-600">Total Points</p>
                  <p className="font-semibold text-purple-900">{totalPoints}</p>
                </div>
                <div>
                  <p className="text-purple-600">Passing Score</p>
                  <p className="font-semibold text-purple-900">{Math.ceil(totalPoints * formData.passing_percentage / 100)}</p>
                </div>
                <div>
                  <p className="text-purple-600">Questions</p>
                  <p className="font-semibold text-purple-900">{formData.mcqs.length} MCQ + {formData.coding_problems.length} Coding</p>
                </div>
                <div>
                  <p className="text-purple-600">Duration</p>
                  <p className="font-semibold text-purple-900">{formData.duration_minutes} minutes</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-between">
              <button
                onClick={() => setStep(3)}
                className="px-6 py-2 border border-gray-200 rounded-lg font-medium hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-8 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
                data-testid="final-create-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Save size={18} />
                    Create Assessment
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CreateAssessmentPage;
